import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import {useAuthStateManager} from '../hooks/useAuthStateManager';
import {useCreateAuthenticatedFetch} from '../api/useCreateAuthenticatedFetch';
import {PersonContext} from '../types';
import {useSignalR} from './SignalRContext';
import baseUrl from '../api/baseUrl.ts';

interface PersonContextType {
    personContext: PersonContext | null;
    isLoading: boolean;
    // Maintainer-seat model: maintainer = billable seat that can create
    // projects and manage people/teams/tags/job-roles; administrator implies
    // maintainer capability. Both default to false until the context loads.
    isMaintainer: boolean;
    isAdministrator: boolean;
    hasPermission: (
        projectId: string,
        permission: 'edit' | 'editResources'
    ) => boolean;
    refreshContext: () => Promise<void>;
    setProjectPermission: (
        projectId: string,
        permission: {canEdit: boolean; canEditResources: boolean}
    ) => void;
}

const PersonContextContext = createContext<PersonContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const usePersonContext = () => {
    const context = useContext(PersonContextContext);

    if (!context) {
        throw new Error(
            'usePersonContext must be used within a PersonContextProvider'
        );
    }

    return context;
};

export const PersonContextProvider: React.FC<{children: ReactNode}> = ({
    children
}) => {
    const [personContext, setPersonContext] = useState<PersonContext | null>(
        null
    );
    const [isLoading, setIsLoading] = useState(true); // Don't block initial load too much?
    const {customerId, personId, isAuthenticated} = useAuthStateManager();
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const {service: signalRService} = useSignalR();

    // Initial load
    useEffect(() => {
        if (
            !isAuthenticated ||
            !customerId ||
            !personId ||
            !authenticatedFetch
        ) {
            return;
        }

        const loadContext = async () => {
            // Only set loading on initial fetch
            if (!personContext) {
                setIsLoading(true);
            }

            try {
                const response = await authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/person-context`
                );

                if (response.ok) {
                    const data = await response.json();
                    setPersonContext(data);
                }
            } catch (error) {
                console.error('Failed to load person context', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadContext();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- personContext is intentionally omitted: it is only read to skip the loading flag on refreshes; including it would refetch on every context update
    }, [isAuthenticated, customerId, personId, authenticatedFetch]);

    const refreshContext = useCallback(async () => {
        if (!authenticatedFetch || !baseUrl) {
            return;
        }

        try {
            const response = await authenticatedFetch(
                `${baseUrl}/api/resource-allocation/person-context`
            );

            if (response.ok) {
                const data = await response.json();
                setPersonContext(data);
            }
        } catch (error) {
            console.error('Failed to refresh person context:', error);
        }
    }, [authenticatedFetch]);

    // SignalR subscription for real-time updates
    useEffect(() => {
        if (!signalRService || !personId) return;

        // Join the person group when the component mounts or personId changes
        const initGroup = async () => {
            await signalRService.joinPersonGroup(personId);
        };

        initGroup();

        const handleRefresh = () => {
            console.log('PersonPermission changed, refreshing context...');
            refreshContext();
        };

        signalRService.on('PersonContextChanged', handleRefresh);
        return () => {
            signalRService.leavePersonGroup(personId);
            signalRService.off('PersonContextChanged', handleRefresh);
        };
    }, [signalRService, personId, refreshContext]);

    // Optimistically set/override the current user's permission for a project.
    // Used right after creating a project (we know the backend made us owner) so
    // edit rights are live immediately, without waiting for a refetch or SignalR.
    // A subsequent refreshContext()/PersonContextChanged reconciles with the server.
    const setProjectPermission = useCallback(
        (
            projectId: string,
            permission: {canEdit: boolean; canEditResources: boolean}
        ) => {
            setPersonContext(prev => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    projectPermissions: {
                        ...prev.projectPermissions,
                        [projectId]: {
                            projectId,
                            canEdit: permission.canEdit,
                            canEditResources: permission.canEditResources
                        }
                    }
                };
            });
        },
        []
    );

    const hasPermission = useCallback(
        (projectId: string, permission: 'edit' | 'editResources'): boolean => {
            if (!personContext) {
                return false;
            }

            const perms = personContext.projectPermissions[projectId];
            if (!perms) {
                return false;
            }

            return permission === 'edit'
                ? perms.canEdit
                : perms.canEditResources;
        },
        [personContext]
    );

    // Memoized so consumers only re-render when the person context actually
    // changes, not on every provider render.
    const value = useMemo(
        () => ({
            personContext,
            isLoading,
            isMaintainer: personContext?.isMaintainer ?? false,
            isAdministrator: personContext?.isAdministrator ?? false,
            hasPermission,
            refreshContext,
            setProjectPermission
        }),
        [
            personContext,
            isLoading,
            hasPermission,
            refreshContext,
            setProjectPermission
        ]
    );

    return (
        <PersonContextContext.Provider value={value}>
            {children}
        </PersonContextContext.Provider>
    );
};
